import {
  CheckCircle2Icon,
  ChevronRightIcon,
  CircleDashedIcon,
  ClockIcon,
  XCircleIcon,
} from "lucide-react";
import React from "react";
import { Button as RACButton } from "react-aria-components";

import { hasPermission } from "@blis/shared/permissions";
import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { badgeStyles } from "@/components/ui/badge";
import {
  DataListItem,
  DataListLabel,
  DataListValue,
} from "@/components/ui/data-list";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Popover } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Text } from "@/components/ui/text";
import {
  NonFocusableTooltipTarget,
  Tooltip,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn, focusRing } from "@/components/ui/utils";
import { useAuth } from "@/features/auth/hooks";
import { rt, useRealtimeInvalidate } from "@/lib/realtime";
import { format } from "@/lib/utils";
import { useIntegration } from "../useIntegration";
import { IntegrationMessageSheet } from "./IntegrationMessageSheet";

type Status = "PENDING" | "SUCCESS" | "FAILED";

type Message = {
  id: number;
  status: Status;
  attempt: number;
  errorMessage: string | null;
  createdAt: string | Date;
};

/**
 * iTransplant integration row for the accession detail sidebar.
 *
 * Renders a separator + `DataListItem` showing send status whenever the
 * integration is enabled for the accession's org — even before any messages
 * have been sent — so users always know reports for this accession will be
 * forwarded. Returns `null` (no row, no separator) when the integration is
 * not enabled for the org.
 *
 * Clicking the badge opens a popover with the most recent activity. Admins
 * can drill into individual messages via {@link IntegrationMessageSheet}.
 */
export function ITransplantSideDetail({
  accessionId,
  orgId,
}: {
  accessionId: number;
  orgId: number;
}) {
  const { user } = useAuth();
  const canViewDetails = hasPermission(user.role, "admin:all");
  const { enabled } = useIntegration("ITRANSPLANT", orgId);
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const { data } = trpc.integrations.messagesForAccession.useQuery(
    { kind: "ITRANSPLANT", accessionId, take: 5 },
    { enabled },
  );

  // Live-update the badge + popover whenever the worker writes a new
  // iTransplant message row for this accession. The realtime stream only
  // delivers an invalidation hint; the actual data still comes from the
  // tRPC query above. Predicate matches on integration kind + accession id
  // so unrelated integrations / accessions don't trigger spurious refetches.
  const utils = trpc.useUtils();
  useRealtimeInvalidate({
    tags: [rt.iTransplantMessages(accessionId)],
    enabled,
    targets: [
      () =>
        utils.integrations.messagesForAccession.invalidate({
          kind: "ITRANSPLANT",
          accessionId,
        }),
    ],
  });

  if (!enabled) return null;

  const latest = data?.latestStatus ?? null;
  const totalCount = data?.totalCount ?? 0;
  const messages = data?.messages ?? [];

  return (
    <>
      <Separator className="col-span-2 my-2" />
      <DataListItem className="items-center!">
        <DataListLabel>
          <TooltipTrigger delay={300}>
            <NonFocusableTooltipTarget>
              <span className="decoration-muted-foreground/40 decoration-dotted underline-offset-2 hover:underline">
                iTransplant
              </span>
            </NonFocusableTooltipTarget>
            <Tooltip>
              Reports for this accession are forwarded to iTransplant
              automatically when finalized.
            </Tooltip>
          </TooltipTrigger>
        </DataListLabel>
        <DataListValue>
          <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
            <RACButton
              aria-label={`iTransplant activity (${totalCount} message${
                totalCount === 1 ? "" : "s"
              })`}
              className={cn(
                badgeStyles({ variant: "outline", size: "sm" }),
                focusRing(),
                "cursor-pointer gap-1 transition-colors hover:bg-accent",
                !latest && "border-transparent text-muted-foreground",
              )}
            >
              {latest && <StatusGlyph status={latest} className="size-3" />}
              <span>{summaryLabel(latest)}</span>
              {totalCount > 0 && (
                <span className="opacity-70">· {totalCount}</span>
              )}
            </RACButton>
            <Popover className="p-0 w-[360px]" placement="bottom end">
              <Dialog hideCloseButton className="gap-0">
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b">
                  <Text size="2" className="font-medium">
                    iTransplant activity
                  </Text>
                  {canViewDetails && (
                    <Link
                      to="/admin/integrations/itransplant"
                      search={{ t: "messages" }}
                      className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
                      onClick={() => setIsOpen(false)}
                    >
                      View all
                      <ChevronRightIcon className="size-3" />
                    </Link>
                  )}
                </div>
                {messages.length === 0 ? (
                  <div className="px-3 py-6 text-center space-y-1">
                    <Text elementType="p" size="2" className="font-medium">
                      Ready to send
                    </Text>
                    <Text elementType="p" size="1" muted>
                      iTransplant is sent automatically when a report is
                      finalized. No action needed.
                    </Text>
                  </div>
                ) : (
                  <ul className="divide-y max-h-[60vh] overflow-y-auto">
                    {messages.map((m) => (
                      <MessageRow
                        key={m.id}
                        message={m}
                        canViewDetails={canViewDetails}
                        onSelect={() => {
                          setSelectedId(m.id);
                          setIsOpen(false);
                        }}
                      />
                    ))}
                  </ul>
                )}
              </Dialog>
            </Popover>
          </DialogTrigger>
        </DataListValue>
      </DataListItem>
      {canViewDetails && (
        <IntegrationMessageSheet
          messageId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </>
  );
}

function MessageRow({
  message,
  canViewDetails,
  onSelect,
}: {
  message: Message;
  canViewDetails: boolean;
  onSelect: () => void;
}) {
  const body = (
    <div className="flex items-start gap-2 w-full text-left">
      <StatusGlyph status={message.status} className="mt-0.5 size-3.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <Text size="2" className="font-medium">
            {labelFor(message.status)}
            {message.attempt > 1 && (
              <Text size="1" muted className="ml-1.5">
                attempt {message.attempt}
              </Text>
            )}
          </Text>
          <Text size="1" mono muted className="shrink-0">
            {format(message.createdAt)}
          </Text>
        </div>
        {message.errorMessage ? (
          <Text
            size="1"
            className={cn(
              "line-clamp-2 wrap-break-word",
              message.status === "SUCCESS"
                ? "text-amber-700 dark:text-amber-300"
                : "text-red-700 dark:text-red-300",
            )}
          >
            {message.errorMessage}
          </Text>
        ) : null}
      </div>
    </div>
  );

  return (
    <li>
      {canViewDetails ? (
        <RACButton
          onPress={onSelect}
          className={cn(
            focusRing(),
            "w-full px-3 py-2 hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer text-left",
          )}
        >
          {body}
        </RACButton>
      ) : (
        <div className="px-3 py-2">{body}</div>
      )}
    </li>
  );
}

function StatusGlyph({
  status,
  className,
}: {
  status: Status | null;
  className?: string;
}) {
  const base = cn("shrink-0", className);
  switch (status) {
    case "SUCCESS":
      return <CheckCircle2Icon className={cn(base, "text-green-600")} />;
    case "FAILED":
      return <XCircleIcon className={cn(base, "text-red-600")} />;
    case "PENDING":
      return <ClockIcon className={cn(base, "text-amber-600")} />;
    default:
      return <CircleDashedIcon className={cn(base, "text-muted-foreground")} />;
  }
}

function summaryLabel(status: Status | null) {
  switch (status) {
    case "SUCCESS":
      return "Sent";
    case "FAILED":
      return "Failed";
    case "PENDING":
      return "Pending";
    default:
      return "Ready";
  }
}

function labelFor(status: Status) {
  switch (status) {
    case "SUCCESS":
      return "Sent";
    case "FAILED":
      return "Failed";
    case "PENDING":
      return "Pending";
  }
}
